import { useGutXAxisImages } from '#components/GutXAxisBody/hooks';
import type { GutXAxisOrgan } from '@repo/types';
import { useMemo } from 'react';
import styles from './GutXAxisOrganList.module.css';
import OrganRow from './OrganRow';
import { byOrganDisplayOrder } from './organOrder';

type GutXAxisOrganListProps = {
  organs: GutXAxisOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /** Omit to render a static list, as the PDF report would. */
  onOrganSelect?: (organ: GutXAxisOrgan) => void;
};

/**
 * The body map as a list, for viewports where the map cannot be hovered or
 * usefully tapped. It resolves its organs through the map's own hook, so it can
 * never name a system the body has no drawing for.
 */
export function GutXAxisOrganList({
  organs,
  age,
  pregnant,
  sex,
  onOrganSelect,
}: GutXAxisOrganListProps) {
  const { systems } = useGutXAxisImages({
    organs,
    age,
    pregnant,
    sex,
    waitForImages: false,
  });

  // Sorted on a copy: the map paints in the order the hook returns.
  const rows = useMemo(
    () => [...systems].sort((a, b) => byOrganDisplayOrder(a.organ, b.organ)),
    [systems],
  );

  return (
    <ul className={styles.list}>
      {rows.map(({ organ }) => (
        <OrganRow
          key={organ.id}
          organ={organ}
          age={age}
          pregnant={pregnant}
          sex={sex}
          onSelect={onOrganSelect ? () => onOrganSelect(organ) : undefined}
        />
      ))}
    </ul>
  );
}

export default GutXAxisOrganList;
